/**
 * High-intent keyword landings for Tools & Bubbles (search queries owners type
 * that are not already covered by industry/size pages in serviceConfigs).
 */
import type { ServicePageConfig } from './serviceConfigs.js';

const whyChoose = [
  {
    icon: 'Shield',
    title: 'Trained professional cleaners',
    description: 'Background-verified staff with supervisor check-ins and quality assurance.',
  },
  {
    icon: 'Sparkles',
    title: 'Eco-friendly 3M chemicals',
    description: 'Safe, effective products for family homes and pets in Kochi’s humidity.',
  },
  {
    icon: 'CheckCircle',
    title: 'Transparent pricing',
    description: 'No hidden charges. See pricing upfront when you book online.',
  },
  {
    icon: 'Star',
    title: '4.8★ Google rated',
    description: 'Same-day slots across Greater Kochi. We re-clean if you are not happy.',
  },
];

const reviews = [
  {
    name: 'Anjali T.',
    location: 'Kakkanad',
    rating: 5,
    text: 'Booked online in minutes. Crew was punctual and the bathrooms actually looked new.',
    date: '2026',
  },
  {
    name: 'Vivek K.',
    location: 'Edappally',
    rating: 5,
    text: 'Same-day slot after a morning booking. Fair price shown before confirm.',
    date: '2026',
  },
  {
    name: 'Priya M.',
    location: 'Elamkulam',
    rating: 5,
    text: 'Professional, insured team. Will keep the fortnightly plan.',
    date: '2026',
  },
];

const KOCHI_AREAS = [
  'Kakkanad',
  'Edappally',
  'Kaloor',
  'Palarivattom',
  'Vyttila',
  'Kadavanthra',
  'Elamkulam',
  'Vennala',
  'Thevara',
  'Vazhakkala',
  'Thripunithura',
  'Aluva',
];

function keywordPage(config: ServicePageConfig): [string, ServicePageConfig] {
  return [config.slug, config];
}

export const KEYWORD_SERVICE_PAGE_CONFIGS: Record<string, ServicePageConfig> = Object.fromEntries([
  keywordPage({
    slug: 'same-day-cleaning-kochi',
    serviceName: 'Same-Day Cleaning Kochi',
    serviceTitle: 'Same-Day Cleaning Kochi',
    hero: {
      eyebrow: 'Book before noon',
      title: 'Same-Day Cleaning Service in Kochi',
      description:
        'Need cleaning today in Kochi? Book same-day deep cleaning, home cleaning, kitchen or bathroom service before noon. No extra same-day fee on the standard rate card.',
      highlights: [
        { icon: 'Clock', text: 'Book before 12 PM' },
        { icon: 'CheckCircle', text: 'No extra same-day fee' },
        { icon: 'Star', text: '4.8★ rated' },
      ],
    },
    servicesIncluded: [
      'Same-day deep cleaning',
      'Kitchen / bathroom emergency clean',
      'Sofa shampoo when slot allows',
      'WhatsApp confirmation in 30 minutes',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book same-day cleaning in Kochi',
      description: 'Slots fill by noon. Call 9995216660 if the calendar is full.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Same-Day Cleaning Kochi | Deep Cleaning Today | Tools & Bubbles',
      metaDescription:
        'Same-day cleaning in Kochi. Book before noon for deep cleaning, home, kitchen or bathroom service today. No extra same-day fee. Call 9995216660.',
      keywords:
        'same day cleaning kochi, same-day cleaning service kochi, cleaning today kochi, emergency cleaning kochi, last minute cleaning kochi',
      introParagraphs: [
        'Same-day cleaning in Kochi is available when you book before noon. Tools & Bubbles holds afternoon and evening crews for Kakkanad, Edappally, Kaloor, Palarivattom, Elamkulam, Vyttila and neighbouring areas.',
        'Guests arriving tonight, a bathroom mould scare, or a post-party kitchen — same-day service uses the same 3M chemicals and Kärcher equipment as a planned visit. There is no same-day surcharge on the published rate card.',
      ],
      sections: [
        {
          heading: 'How same-day cleaning works',
          paragraphs: [
            'Book online or call 9995216660 before 12 PM, pick an afternoon or evening window, and get confirmation within 30 minutes. Crews carry equipment — you do not supply mops or chemicals.',
          ],
          bulletList: [
            'Book before noon for the best slot choice',
            'Same quality as scheduled jobs',
            'Photo update on WhatsApp when complete',
          ],
        },
        {
          heading: 'Same-day coverage in Kochi',
          paragraphs: [
            'Core Ernakulam (Kakkanad to Fort Kochi, Aluva to Thripunithura) is routinely same-day. Outlying towns depend on routing — we will tell you immediately if the next slot is tomorrow morning.',
          ],
        },
      ],
      conclusion: 'Need cleaning today in Kochi? Book same-day service before noon or call 9995216660.',
    },
    faq: [
      {
        question: 'Can I get same-day house cleaning in Kochi?',
        answer:
          'Yes, when you book before noon and a crew is free in your locality. Call 9995216660 for the fastest confirmation.',
      },
      {
        question: 'Is same-day cleaning more expensive?',
        answer: 'No. Same-day uses the same online rate card. You pay for the service, not a panic fee.',
      },
    ],
    images: {
      hero: '/images/hero-deep-cleaning-kochi.png',
    },
  }),
  keywordPage({
    slug: 'water-tank-cleaning-kochi',
    serviceName: 'Water Tank Cleaning Kochi',
    serviceTitle: 'Water Tank Cleaning Kochi',
    hero: {
      eyebrow: 'Overhead & underground tanks',
      title: 'Water Tank Cleaning in Kochi',
      description:
        'Professional water tank cleaning in Kochi — drain, scrub, disinfectant rinse and refill guidance for overhead and underground tanks in apartments and villas.',
      highlights: [
        { icon: 'Shield', text: 'Disinfectant rinse' },
        { icon: 'CheckCircle', text: 'Apartment & villa' },
        { icon: 'Star', text: '4.8★ rated' },
      ],
    },
    servicesIncluded: [
      'Tank drain and sludge removal',
      'Interior scrub',
      'Food-grade disinfectant rinse',
      'Photo report for associations',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book water tank cleaning in Kochi',
      description: 'Apartment associations and villas. Call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Water Tank Cleaning Kochi | Overhead & Underground | Tools & Bubbles',
      metaDescription:
        'Water tank cleaning in Kochi for apartments and villas. Drain, scrub, disinfectant rinse. Association photo reports. Call 9995216660.',
      keywords:
        'water tank cleaning kochi, overhead tank cleaning kochi, underground water tank cleaning kochi, sintex tank cleaning kochi',
      introParagraphs: [
        'Water tank cleaning in Kochi should happen at least twice a year — more often after monsoon if you see sediment or a musty smell at the tap. Tools & Bubbles drains, scrubs and disinfectant-rinses overhead and underground tanks for apartments and villas.',
        'Associations in Kakkanad, Infopark-side Vazhakkala, Marine Drive towers and Thripunithura villas book us with a photo report for their minutes. Combine tank cleaning with a home deep clean on the same visit when the water supply can be paused.',
      ],
      sections: [
        {
          heading: 'What water tank cleaning includes',
          paragraphs: [
            'Emptying, sludge removal, wall and floor scrub, disinfectant contact time, rinse and guidance on when to refill. We do not chemically “soften” municipal water — we clean the vessel so stored water stays healthier.',
          ],
        },
        {
          heading: 'Apartment vs villa tanks in Kochi',
          paragraphs: [
            'High-rises often have terrace overhead tanks plus a sump. Villas may have a Sintex and an underground tank. Tell us the type when you book so the crew brings the right access gear.',
          ],
        },
      ],
      conclusion: 'Book water tank cleaning in Kochi. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'How often should I clean my water tank in Kochi?',
        answer:
          'Every 6 months is a practical default; after heavy monsoon or if taps smell musty, book sooner.',
      },
      {
        question: 'Do you clean underground sumps?',
        answer: 'Yes, underground and overhead tanks, subject to safe access. Share photos when you book.',
      },
    ],
    images: {
      hero: '/images/hero-deep-cleaning.png',
    },
  }),
  keywordPage({
    slug: 'window-cleaning-kochi',
    serviceName: 'Window Cleaning Kochi',
    serviceTitle: 'Window Cleaning Kochi',
    hero: {
      eyebrow: 'Glass, tracks & high-rise interiors',
      title: 'Window Cleaning Service in Kochi',
      description:
        'Interior window and glass cleaning in Kochi apartments and villas — panes, tracks, grills and balcony glass. High-rise interior work without risky exterior high-rise dangling.',
      highlights: [
        { icon: 'Sparkles', text: 'Tracks & panes' },
        { icon: 'MapPin', text: 'Apartment interiors' },
        { icon: 'Star', text: '4.8★ rated' },
      ],
    },
    servicesIncluded: [
      'Interior glass and window panes',
      'Track and sill dust removal',
      'Grill wipe-down',
      'Balcony glass (safe access)',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book window cleaning in Kochi',
      description: 'Add to a deep clean or book glass-only. Call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Window Cleaning Kochi | Glass & Track Cleaning | Tools & Bubbles',
      metaDescription:
        'Window cleaning in Kochi for apartments and villas. Interior glass, tracks, grills and balcony panes. Book with a deep clean. Call 9995216660.',
      keywords:
        'window cleaning kochi, glass cleaning kochi, apartment window cleaning kochi, balcony glass cleaning kochi',
      introParagraphs: [
        'Window cleaning in Kochi is not just a spray-and-wipe. Coastal humidity plus Infopark-side construction dust packs tracks and balcony glass. Tools & Bubbles cleans interior panes, sills, grills and reachable balcony glass as a standalone add-on or with a full deep clean.',
        'We do not offer rope-access exterior facade work. For high-rises we clean everything safely reachable from inside and the balcony. Marine Drive, Kundanoor, Kakkanad and Elamkulam apartments book this most often before festivals or after AC season dust.',
      ],
      sections: [
        {
          heading: 'What is included in window cleaning?',
          paragraphs: [
            'Interior glass, window tracks, sills, inner frames, grills and balcony glass where access is safe. Sticker and paint-splatter removal from renovations can be added.',
          ],
        },
      ],
      conclusion: 'Book window and glass cleaning in Kochi. Call 9995216660.',
    },
    faq: [
      {
        question: 'Do you clean exterior high-rise windows?',
        answer:
          'We clean interior glass and balcony-reachable panes. Rope-access facade work is not offered — that needs a specialist high-rise contractor.',
      },
    ],
    images: {
      hero: '/images/hero-home.png',
    },
  }),
  keywordPage({
    slug: 'housekeeping-services-kochi',
    serviceName: 'Housekeeping Services Kochi',
    serviceTitle: 'Housekeeping Services Kochi',
    hero: {
      eyebrow: 'Homes, offices & clinics',
      title: 'Housekeeping Services in Kochi',
      description:
        'Professional housekeeping services in Kochi for homes, offices, clinics and small commercial spaces. Daily, weekly or contract cleaning with trained crews.',
      highlights: [
        { icon: 'CheckCircle', text: 'Home & office' },
        { icon: 'Clock', text: 'Contract or one-time' },
        { icon: 'Star', text: '4.8★ rated' },
      ],
    },
    servicesIncluded: [
      'Home housekeeping plans',
      'Office pantry & workstation clean',
      'Clinic waiting-area hygiene',
      'Restroom sanitisation',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book housekeeping services in Kochi',
      description: 'Homes, offices and clinics. Call 9995216660 for a contract quote.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Housekeeping Services Kochi | Home & Office | Tools & Bubbles',
      metaDescription:
        'Housekeeping services in Kochi for homes, offices and clinics. Daily, weekly or contract cleaning. Trained crews. Call 9995216660.',
      keywords:
        'housekeeping services kochi, housekeeping kochi, office housekeeping kochi, home housekeeping kochi, janitorial kochi',
      introParagraphs: [
        'Housekeeping services in Kochi from Tools & Bubbles cover both homes and workplaces. Families book weekly housekeeping; offices in Infopark, Kakkanad, MG Road and Elamkulam book pantry, workstation and restroom contracts.',
        'Unlike informal housekeepers, our crews bring chemicals and machines, follow a checklist, and can work after business hours so your team is not disrupted.',
      ],
      sections: [
        {
          heading: 'Home vs office housekeeping',
          paragraphs: [
            'Home housekeeping is living-space focused (kitchen, bathrooms, floors). Office housekeeping adds pantry, meeting rooms, restrooms and after-hours access. We write the scope into the booking so invoices stay clean for your accountant.',
          ],
        },
      ],
      conclusion: 'Book housekeeping services in Kochi. Call 9995216660.',
    },
    faq: [
      {
        question: 'Do you offer office housekeeping in Kochi?',
        answer:
          'Yes. Daily or weekly office housekeeping for Infopark, Kakkanad, MG Road and other commercial areas. Call 9995216660 for a contract quote.',
      },
      {
        question: 'Do you provide maid service or maid placement?',
        answer:
          'No. Tools & Bubbles does not place maids or offer live-in domestic help. We send a professional cleaning crew for booked jobs — with chemicals, machines and a checklist.',
      },
    ],
    images: {
      hero: '/images/hero-deep-cleaning.png',
    },
  }),
  keywordPage({
    slug: 'professional-cleaners-kochi',
    serviceName: 'Professional Cleaners Kochi',
    serviceTitle: 'Professional Cleaners Kochi',
    hero: {
      eyebrow: 'Trained, insured, equipment included',
      title: 'Professional Cleaners in Kochi',
      description:
        'Hire professional cleaners in Kochi — background-verified staff, 3M chemicals, Kärcher machines, insurance and a satisfaction guarantee. Book deep cleaning or regular home cleaning online.',
      highlights: [
        { icon: 'Shield', text: 'Insured team' },
        { icon: 'Sparkles', text: '3M + Kärcher' },
        { icon: 'Star', text: '4.8★ Google' },
      ],
    },
    servicesIncluded: [
      'Background-verified cleaners',
      'Supervisor check-ins',
      'Professional machines included',
      'Workmen compensation & liability cover',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Hire professional cleaners in Kochi',
      description: 'See pricing online. Call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Professional Cleaners Kochi | Insured Cleaning Crew | Tools & Bubbles',
      metaDescription:
        'Professional cleaners in Kochi. Insured, background-verified staff with 3M chemicals and Kärcher equipment. Book deep or regular cleaning. Call 9995216660.',
      keywords:
        'professional cleaners kochi, professional cleaning service kochi, hire cleaners kochi, best cleaners kochi, cleaning company kochi',
      introParagraphs: [
        'Professional cleaners in Kochi means more than cheap labour. Tools & Bubbles sends background-verified staff with insurance, branded chemicals and machines — the difference you notice on grout, chimneys and sofa fabric.',
        'If you searched “professional cleaners near me Kochi”, compare Google reviews, insurance and whether equipment is included. We publish pricing online so you are not negotiating at the door.',
      ],
      sections: [
        {
          heading: 'What makes a cleaner “professional” in Kochi?',
          paragraphs: [
            'Training, ID, insurance, a written scope, the right chemistry for Kerala kitchens, and a way to re-clean if something is missed. That is the Tools & Bubbles standard across Kakkanad, Edappally, Elamkulam and 30+ localities.',
          ],
        },
      ],
      conclusion: 'Hire professional cleaners in Kochi. Book online or call 9995216660.',
    },
    faq: [
      {
        question: 'Are your cleaners background verified?',
        answer:
          'Yes. Staff are background-verified, and jobs are insured under workmen compensation and public liability cover.',
      },
    ],
    images: {
      hero: '/images/hero-deep-cleaning-kochi.png',
    },
  }),
  keywordPage({
    slug: 'monsoon-cleaning-kochi',
    serviceName: 'Monsoon Cleaning Kochi',
    serviceTitle: 'Monsoon Cleaning Kochi',
    hero: {
      eyebrow: 'Anti-fungal bathrooms & humidity reset',
      title: 'Monsoon Cleaning Service in Kochi',
      description:
        'Monsoon cleaning in Kochi: bathroom anti-fungal treatment, mould-prone corners, balcony moss and kitchen humidity film. Book before or during Kerala monsoon.',
      highlights: [
        { icon: 'Sparkles', text: 'Anti-fungal bathrooms' },
        { icon: 'Clock', text: 'Pre- & mid-monsoon' },
        { icon: 'Star', text: '4.8★ rated' },
      ],
    },
    servicesIncluded: [
      'Bathroom grout anti-fungal',
      'Mould-prone corner treatment',
      'Balcony moss wipe',
      'Kitchen humidity film degrease',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book monsoon cleaning in Kochi',
      description: 'Pre-monsoon slots fill in May–June. Call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Monsoon Cleaning Kochi | Anti-Fungal Bathroom Deep Clean | Tools & Bubbles',
      metaDescription:
        'Monsoon cleaning in Kochi. Bathroom anti-fungal, mould corners, balcony moss. Pre-monsoon and mid-season slots. Call 9995216660.',
      keywords:
        'monsoon cleaning kochi, monsoon deep cleaning kochi, bathroom fungus cleaning kochi, mould cleaning kochi, pre monsoon cleaning kochi',
      introParagraphs: [
        'Monsoon cleaning in Kochi is a bathroom-and-balcony problem more than a dusting problem. Humidity feeds fungus on grout, window corners and wardrobe backs. Tools & Bubbles runs a monsoon-focused deep clean with anti-fungal bathroom treatment.',
        'Book in May–June before the heavy rains, or mid-season if you already see black grout. Homes in Thevara, Nettoor, Cheranalloor and waterfront Marine Drive need this most; inland Kakkanad apartments still get AC-related mould.',
      ],
      sections: [
        {
          heading: 'What monsoon cleaning includes',
          paragraphs: [
            'Full deep clean plus extra dwell time on bathrooms, balcony moss, window tracks and kitchen film. Optional mattress and sofa shampoo if the house smells musty.',
          ],
        },
      ],
      conclusion: 'Book monsoon cleaning in Kochi. Call 9995216660.',
    },
    faq: [
      {
        question: 'When should I book monsoon cleaning in Kochi?',
        answer:
          'Best in May–June before peak rains, or as soon as you see bathroom fungus. Mid-monsoon slots are available but bathrooms need longer dry time.',
      },
    ],
    images: {
      hero: '/images/hero-deep-cleaning.png',
    },
  }),
  keywordPage({
    slug: 'house-cleaning-near-me-kochi',
    serviceName: 'House Cleaning Near Me Kochi',
    serviceTitle: 'House Cleaning Near Me Kochi',
    hero: {
      eyebrow: 'Local crews across Greater Kochi',
      title: 'House Cleaning Near Me in Kochi',
      description:
        'Searched “house cleaning near me” in Kochi? Tools & Bubbles has local crews for Kakkanad, Edappally, Elamkulam, Vennala, Thevara, Vazhakkala and 30+ neighbourhoods. Book the area page that matches your pin.',
      highlights: [
        { icon: 'MapPin', text: '30+ Kochi localities' },
        { icon: 'Clock', text: 'Same-day in core areas' },
        { icon: 'Star', text: '4.8★ Google' },
      ],
    },
    servicesIncluded: [
      'Home deep cleaning near you',
      'Regular house cleaning plans',
      'Kitchen & bathroom focus',
      'Area-specific landing pages',
    ],
    areasWeServe: KOCHI_AREAS,
    whyChoose,
    reviews,
    cta: {
      title: 'Book house cleaning near you in Kochi',
      description: 'Pick your locality or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'House Cleaning Near Me Kochi | Local Deep Cleaning | Tools & Bubbles',
      metaDescription:
        'House cleaning near me in Kochi. Local crews in Kakkanad, Edappally, Elamkulam, Vennala, Thevara and 30+ areas. Same-day slots. Call 9995216660.',
      keywords:
        'house cleaning near me kochi, home cleaning near me kochi, cleaning service near me kochi, deep cleaning near me kochi',
      introParagraphs: [
        '“House cleaning near me” in Kochi should show a crew that actually routes your neighbourhood — not a call centre in another city. Tools & Bubbles is based in Kochi and publishes dedicated pages for Kakkanad, Edappally, Elamkulam, Vennala, Thevara, Vazhakkala, Thammanam and more.',
        'Google uses your location; we match it with a local landing page, transparent pricing and same-day slots in the core city. Open your area page or book from this hub if you are unsure of the locality name.',
      ],
      sections: [
        {
          heading: 'Find your Kochi locality page',
          paragraphs: [
            'Choose the neighbourhood closest to your pin: Kakkanad, Edappally, Kaloor, Palarivattom, Vyttila, Kadavanthra, Elamkulam, Vennala, Thevara, Palluruthy, Vazhakkala, Thammanam, Elamakkara, Cheranalloor, Nettoor or Irumpanam.',
          ],
        },
      ],
      conclusion: 'Book house cleaning near you in Kochi. Call 9995216660.',
    },
    faq: [
      {
        question: 'Do you really serve my area if I searched house cleaning near me?',
        answer:
          'If you are in Greater Kochi / Ernakulam, almost certainly yes. Call 9995216660 with your landmark and we confirm the slot.',
      },
    ],
    images: {
      hero: '/images/hero-home.png',
    },
  }),
]);
